import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReusableInput from "./reusableInput";
import { skillsUsed } from "../../../../../utils/constants";

export interface SkillData {
  skill: string;
  level: string;
  experience: string;
  lastUsed: string;
  _id?: string;
  skillId?: string;
  referenceSkillId?: string;
  isSaved?: boolean;
  isEdit?: boolean;
}

export interface APISkill {
  _id: string;
  title: string;
  rating: number;
  years: number;
  lastUsed: number;
}

interface SkillInputProps {
  addUpdateSkill: (data: SkillData) => Promise<boolean | void>;
  data?: SkillData;
}

const SkillInput: React.FC<SkillInputProps> = ({ addUpdateSkill, data }) => {
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<SkillData>({
    defaultValues: { ...data, lastUsed: "Current" },
  });

  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({
    skill: true,
    level: true,
    experience: true,
    lastUsed: true,
  });

  const onSave = async (field: SkillData) => {
    const isValid = await trigger();

    if (isValid) {
      const success = await addUpdateSkill(field);
      if (success) {
        reset({ skill: "", level: "", experience: "", lastUsed: "Current" });
        setValidationErrors({
          skill: true,
          level: true,
          experience: true,
          lastUsed: true,
        });
      }
    } else {
      setValidationErrors({
        skill: !errors.skill,
        level: !errors.level,
        experience: !errors.experience,
        lastUsed: !errors.lastUsed,
      });
    }
  };

  return (
    <div className="bg-[#FAFAFA] rounded-[20px] py-5 px-20">
      <div className="grid grid-cols-[1.9fr_0.75fr_0.6fr_1fr_0.5fr]">
        {["Skill", "Rating", "Years", "When"].map((label) => (
          <span key={label} className="text-xs font-semibold text-gray-700">
            {label}
          </span>
        ))}
        <div className="w-16" />
      </div>

      <div className="grid grid-cols-[1.7fr_0.68fr_0.58fr_1fr_0.5fr] gap-x-3 py-3 items-center">
        {[
          {
            name: "skill",
            type: "skill",
            rules: { required: "Skill is required" },
          },
          {
            name: "level",
            type: "rating",
            rules: { required: "Rating is required" },
            placeholder: "8",
          },
          {
            name: "experience",
            placeholder: "Yrs",
            rules: { required: "Experience is required" },
          },
          {
            name: "lastUsed",
            type: "lastUsed",
            rules: { required: "Last used is required" },
            placeholder: "Skill last used",
          },
        ].map(({ name, type = "text", placeholder, rules }) => (
          <div key={name}>
            <Controller
              name={name as keyof SkillData}
              control={control}
              rules={rules}
              render={({ field: { value, onChange } }) => (
                <ReusableInput
                  type={type}
                  placeholder={placeholder}
                  value={value}
                  onChange={onChange}
                  options={skillsUsed}
                  isValid={validationErrors[name]}
                  onSelect={name === "skill" ? (item) => {
                    const referenceSkillId = item?.referenceSkillId?._id || item?.referenceSkillId || item?._id;
                    setValue("skillId", item?.isReferenceSkill ? undefined : item?._id);
                    setValue("referenceSkillId", item?.isReferenceSkill ? referenceSkillId : undefined);
                  } : undefined}
                />
              )}
            />
          </div>
        ))}

        <button
          onClick={handleSubmit(onSave)}
          className="bg-primary text-white text-xs font-medium rounded-md px-5 py-1.5 hover:bg-black"
        >
          Add
        </button>
      </div>
    </div>
  );
};

export default SkillInput;
